import React, { useEffect, useRef, useState } from 'react';
import { FolderOpen, Building2, CheckCircle2, ArrowUpRight, X, Calendar, Users, Target, Award, Lightbulb, TrendingUp } from 'lucide-react';

// Project Detail Modal
const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#0a1628]/95 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-[#1a2744] border border-[#d4af37]/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-[#0a1628] border border-[#d4af37]/20 flex items-center justify-center text-[#94a3b8] hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-all rounded-lg"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="p-8 border-b border-[#d4af37]/20 bg-gradient-to-r from-[#0a1628] to-[#1a2744]">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-[#d4af37]/10 flex items-center justify-center rounded-xl flex-shrink-0">
              <FolderOpen size={40} className="text-[#d4af37]" />
            </div>
            <div className="space-y-3">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
                {project.title}
              </h2>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-[#d4af37]">
                  <Building2 size={16} />
                  <span className="font-semibold">{project.company}</span>
                </div>
                {project.duration && (
                  <div className="flex items-center gap-2 text-[#64748b]">
                    <Calendar size={16} />
                    <span>{project.duration}</span>
                  </div>
                )}
              </div>
              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="bg-[#d4af37]/10 text-[#d4af37] text-sm font-medium px-3 py-1 rounded-full border border-[#d4af37]/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-8">
          {/* Overview */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-lg font-bold text-white">
              <Target size={20} className="text-[#d4af37]" />
              Project Overview
            </h3>
            <p className="text-[#94a3b8] leading-relaxed text-lg">
              {project.description}
            </p>
          </div>

          {/* Challenge & Solution */}
          {project.challenge && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#0a1628] p-6 rounded-xl border border-[#d4af37]/10">
                <h4 className="flex items-center gap-2 text-white font-semibold mb-3">
                  <Lightbulb size={18} className="text-[#d4af37]" />
                  The Challenge
                </h4>
                <p className="text-[#94a3b8] text-sm leading-relaxed">
                  {project.challenge}
                </p>
              </div>
              <div className="bg-[#0a1628] p-6 rounded-xl border border-[#d4af37]/10">
                <h4 className="flex items-center gap-2 text-white font-semibold mb-3">
                  <TrendingUp size={18} className="text-[#d4af37]" />
                  The Solution
                </h4>
                <p className="text-[#94a3b8] text-sm leading-relaxed">
                  {project.solution}
                </p>
              </div>
            </div>
          )}

          {/* Key Achievements */}
          {project.achievements && (
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                <Award size={20} className="text-[#d4af37]" />
                Key Achievements & Impact
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {project.achievements.map((achievement, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 bg-[#0a1628] p-4 rounded-lg border border-[#d4af37]/10"
                  >
                    <CheckCircle2 size={18} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#94a3b8] text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Role & Responsibilities */}
          {project.role && (
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                <Users size={20} className="text-[#d4af37]" />
                My Role & Contributions
              </h3>
              <div className="bg-[#0a1628] p-6 rounded-xl border border-[#d4af37]/10">
                <p className="text-[#94a3b8] leading-relaxed">
                  {project.role}
                </p>
              </div>
            </div>
          )}

          {/* Technologies/Tools */}
          {project.technologies && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white">Technologies & Tools</h3>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, idx) => (
                  <span
                    key={idx}
                    className="bg-[#1a2744] text-[#94a3b8] text-sm px-4 py-2 rounded-lg border border-[#d4af37]/10"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#d4af37]/20 bg-[#0a1628]/50">
          <button
            onClick={onClose}
            className="w-full bg-[#d4af37] text-[#0a1628] py-3 font-semibold hover:bg-[#e8c547] transition-colors rounded-lg"
          >
            Close Project Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Project Card
const ProjectCard = ({ project, index, isVisible, onViewDetails }) => {
  return (
    <div 
      className={`group relative bg-[#1a2744] border border-[#d4af37]/10 hover:border-[#d4af37]/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
      style={{ animationDelay: `${index * 0.15}s` }}
      onClick={() => onViewDetails(project)}
    >
      {/* Gold accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#d4af37] via-[#e8c547] to-[#d4af37] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />

      {/* Header */}
      <div className="p-6 border-b border-[#d4af37]/10">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="w-16 h-16 bg-[#d4af37]/10 flex items-center justify-center rounded-xl group-hover:bg-[#d4af37] transition-colors duration-300">
            <FolderOpen size={32} className="text-[#d4af37] group-hover:text-[#0a1628] transition-colors" />
          </div>
          <div className="flex items-center gap-2 text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-sm font-medium">View Details</span>
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </div>
        </div>

        <h3 className="text-xl md:text-2xl font-bold text-white group-hover:text-[#d4af37] transition-colors mb-2">
          {project.title}
        </h3>

        <div className="flex items-center gap-2 text-[#d4af37] text-sm">
          <Building2 size={14} />
          <span className="font-medium">{project.company}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <p className="text-[#94a3b8] leading-relaxed text-sm line-clamp-3">
          {project.description}
        </p>

        {/* Quick Stats */}
        {project.achievements && (
          <div className="flex items-center gap-2 text-[#64748b] text-xs">
            <CheckCircle2 size={14} className="text-[#d4af37]" />
            <span>{project.achievements.length} Key Achievements</span>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 pt-2">
          {project.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="bg-[#0a1628] text-[#d4af37] text-xs font-medium px-3 py-1.5 border border-[#d4af37]/20 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Click to expand hint */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0a1628] to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-center text-[#d4af37] text-sm font-medium">
          Click to view full project details →
        </div>
      </div>
    </div>
  );
};

const Projects = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section id="projects" ref={sectionRef} className="py-24 md:py-32 bg-[#0f1d32] relative">
        {/* Background accent */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#d4af37]/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Section Header */}
          <div className={`mb-16 text-center ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <span className="inline-block bg-[#d4af37]/10 text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-4">
              Featured Work
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
              Key <span className="text-[#d4af37]">Projects</span>
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mt-6 mx-auto" />
            <p className="text-[#94a3b8] mt-6 max-w-3xl mx-auto text-lg">
              Award-winning initiatives that transformed wealth management operations and delivered exceptional client experiences. 
              <span className="text-[#d4af37]"> Click on any project</span> to explore the full case study.
            </p>
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {data.map((project, index) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                index={index} 
                isVisible={isVisible}
                onViewDetails={setSelectedProject}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      <ProjectModal 
        project={selectedProject}
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </>
  );
};

export default Projects;
